import { useCallback, useRef, useState } from "react";
import { Upload, X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/cloudinary";

const MAX_MB = 3;
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

/**
 * ImageUpload — drop-in replacement for image URL text inputs.
 * Stores a data-URL (base64) in the same string field, so backend storage
 * remains untouched. Supports upload / replace / delete / preview / drag &
 * drop. Cropping is a light square/free choice via `aspect` prop
 * (frontend-only) — no external crop lib.
 *
 * Props:
 *  - value: string (data URL or hosted URL)
 *  - onChange: (dataUrl|"") => void
 *  - label?: string
 *  - hint?: string
 *  - aspect?: "square" | "banner" | "free"  (visual container ratio only)
 *  - compact?: boolean (sleek, horizontal compact layout for logos/avatars)
 *  - testId?: string
 */
export default function ImageUpload({
  value = "",
  onChange,
  label = "Image",
  hint,
  aspect = "square",
  compact = false,
  testId,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const ratioClass = aspect === "banner" ? "aspect-[16/6]" : aspect === "free" ? "min-h-[160px]" : "aspect-square";

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    const mb = file.size / (1024 * 1024);
    if (mb > MAX_MB) { toast.error(`Image is too large (${mb.toFixed(1)} MB) — max ${MAX_MB} MB`); return; }
    setBusy(true);
    // Sprint 76 — always try Cloudinary first so we store an https:// URL
    // (not base64). Falls back to a local data-URL if the backend has no
    // Cloudinary creds (dev/preview) so the flow keeps working.
    try {
      const res = await uploadToCloudinary(file, { folder: "babu-baazar/products" });
      if (res?.url) {
        onChange?.(res.url);
        setBusy(false);
        return;
      }
    } catch (err) {
      // Only fall back for a "not configured" 501 — surface other errors.
      const status = err?.response?.status;
      if (status && status !== 501 && status !== 500) {
        toast.error(err?.response?.data?.error?.message || err?.message || "Upload failed");
        setBusy(false);
        return;
      }
      toast.message("Cloudinary not configured — keeping image locally.", { duration: 2500 });
    }
    const reader = new FileReader();
    reader.onload = () => { onChange?.(String(reader.result || "")); setBusy(false); };
    reader.onerror = () => { toast.error("Could not read file"); setBusy(false); };
    reader.readAsDataURL(file);
  }, [onChange]);

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const clear = (e) => { e?.stopPropagation(); onChange?.(""); if (inputRef.current) inputRef.current.value = ""; };

  if (compact) {
    return (
      <div className="space-y-1.5" data-testid={testId}>
        {label && <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</label>}
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex items-center gap-4 p-3.5 rounded-xl border transition ${
            dragging ? "border-primary bg-primary/5" : "border-border bg-card/50"
          }`}
          data-testid={`${testId || "img-upload"}-dropzone`}
        >
          {/* Avatar / Logo preview box */}
          <div
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl border-2 border-dashed border-border hover:border-primary/60 bg-muted/40 overflow-hidden cursor-pointer flex-shrink-0 grid place-items-center group transition"
          >
            {value ? (
              <>
                <img
                  src={value}
                  alt="preview"
                  className="w-full h-full object-cover"
                  data-testid={`${testId || "img-upload"}-preview`}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <span className="text-[11px] font-medium text-white">Change</span>
                </div>
              </>
            ) : (
              <div className="text-center p-2">
                {busy ? (
                  <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <ImagePlus className="w-6 h-6" />
                    <span className="text-[10px] font-medium">Upload</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Details & Actions */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition shadow-sm"
                data-testid={`${testId || "img-upload"}-pick-btn`}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {value ? "Change Logo" : "Upload Logo"}
              </button>
              {value && (
                <button
                  type="button"
                  onClick={clear}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-destructive/30 text-destructive bg-destructive/5 hover:bg-destructive/10 transition"
                  data-testid={`${testId || "img-upload"}-delete`}
                >
                  <X className="w-3.5 h-3.5" />
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {hint || `PNG, JPG, WebP or SVG up to ${MAX_MB} MB. Square format recommended.`}
            </p>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onPick}
          className="hidden"
          data-testid={`${testId || "img-upload"}-input`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid={testId}>
      {label && <label className="text-sm font-medium">{label}</label>}
      <div
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        className={`relative w-full ${ratioClass} rounded-xl border-2 border-dashed transition cursor-pointer overflow-hidden group
          ${dragging ? "border-primary bg-primary/5" : value ? "border-transparent" : "border-border hover:border-primary/50 bg-secondary/40"}`}
        data-testid={`${testId || "img-upload"}-dropzone`}
      >
        {value ? (
          <>
            <img src={value} alt="preview" className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId || "img-upload"}-preview`} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                  className="rounded-full bg-white/90 text-foreground px-3 py-1.5 text-xs font-semibold hover:bg-white"
                  data-testid={`${testId || "img-upload"}-replace`}
                >
                  Replace
                </button>
                <button
                  type="button"
                  onClick={clear}
                  className="rounded-full bg-white/90 text-destructive px-3 py-1.5 text-xs font-semibold hover:bg-white"
                  data-testid={`${testId || "img-upload"}-delete`}
                >
                  <X className="w-3 h-3 inline mr-1" />Remove
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 text-muted-foreground">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : (
              <>
                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center mb-2">
                  <ImagePlus className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-foreground">
                  {dragging ? "Drop image here" : "Click or drag & drop"}
                </div>
                <div className="text-[11px] mt-1">PNG · JPG · WebP · SVG (max {MAX_MB} MB)</div>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onPick}
          className="hidden"
          data-testid={`${testId || "img-upload"}-input`}
        />
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
